import type { TopicScore } from '../components/AIExam/AIExam.utils';

export type ReadinessLevel = 'low' | 'medium' | 'high';

export type ReadinessSummary = {
  readinessPercent: number;
  level: ReadinessLevel;
  /** 0-3 topic IDs, weakest first */
  studyTonightTopicIds: string[];
  /** true when we have any real practice data */
  hasRealData: boolean;
};

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

const CRITICAL_TOPIC_IDS = new Set<string>([
  'priority-rules',
  'traffic-lights-signals',
  'speed-limits',
  'sign-identification',
  'road-markings',
  'motorway-rules',
]);

/**
 * v1 readiness: weighted average of topic accuracies.
 * - Critical topics count 2x
 * - Missing topics default to 50% (already handled in TopicScore builder)
 */
export function computeReadiness(topicScores: TopicScore[]): ReadinessSummary {
  const safe = Array.isArray(topicScores) ? topicScores : [];
  const hasRealData = safe.some((t) => t.hasRealData);

  const weights = safe.map((t) => (CRITICAL_TOPIC_IDS.has(t.topicId) ? 2 : 1));
  const weightedTotal = safe.reduce((sum, t, i) => sum + (t.accuracy * (weights[i] ?? 1)), 0);
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
  const raw = Math.round(weightedTotal / weightSum);
  const readinessPercent = clamp(raw, 0, 100);

  const level: ReadinessLevel =
    readinessPercent >= 80 ? 'high' : readinessPercent >= 60 ? 'medium' : 'low';

  const studyTonightTopicIds = [...safe]
    .filter((t) => t.hasRealData)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, 3)
    .map((t) => t.topicId);

  return { readinessPercent, level, studyTonightTopicIds, hasRealData };
}

